import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Edit3, FilePlus2, Mail, Plus, Printer, Trash2, Copy, ReceiptText, WalletCards, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/pages/Finance";
import api, { backendUrl, formatApiErrorDetail } from "@/lib/api";
import { INVOICES } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";

const inputCls = "w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors";

const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0, discount: 0, tax: 0 });
const emptyForm = (template = null) => ({
    currency: "USD", billing_name: "", billing_contact: "", billing_email: "", billing_phone: "", billing_address: "", billing_tax_id: "", supplier_tax_id: "", purchase_order: "", place_of_supply: "", bank_details: "",
    invoice_number: "",
    client_id: "",
    project_id: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    status: "draft",
    payment_terms: "",
    notes: "",
    items: [emptyItem()],
    payments: [],
    company_logo: template?.company_logo || "",
    company_details: template?.company_details || "",
    color_theme: template?.color_theme || "",
    footer: template?.footer || "",
    terms_and_conditions: template?.terms_conditions || "",
    invoice_layout: template?.invoice_layout || "",
});

const invoiceTemplateFields = ["company_logo", "company_details", "color_theme", "footer", "terms_and_conditions", "invoice_layout"];
const THEME_PRESETS = [
    { label: "Blue", value: "#1d4ed8" },
    { label: "Indigo", value: "#4338ca" },
    { label: "Teal", value: "#0f766e" },
    { label: "Emerald", value: "#059669" },
    { label: "Orange", value: "#ea580c" },
    { label: "Rose", value: "#e11d48" },
];
const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "-issue_date", label: "Latest issue date" },
    { value: "issue_date", label: "Earliest issue date" },
    { value: "-due_date", label: "Latest due date" },
    { value: "due_date", label: "Earliest due date" },
    { value: "invoice_number", label: "Invoice number A-Z" },
    { value: "status", label: "Status A-Z" },
];

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
});

const normalizeInvoiceForm = (invoice, template) => {
    const defaults = emptyForm(template);
    if (!invoice) return defaults;
    const base = {
        ...defaults,
        ...invoice,
        issue_date: invoice.issue_date?.slice(0, 10) || defaults.issue_date,
        due_date: invoice.due_date?.slice(0, 10) || defaults.due_date,
        terms_and_conditions: invoice.terms_and_conditions || invoice.terms_conditions || defaults.terms_and_conditions,
    };
    invoiceTemplateFields.forEach((k) => {
        if (!base[k] && defaults[k]) base[k] = defaults[k];
    });
    return base;
};

function calcTotals(form) {
    const subtotal = (form.items || []).reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.unit_price || 0)), 0);
    const discount = (form.items || []).reduce((sum, item) => sum + Number(item.discount || 0), 0);
    const tax = (form.items || []).reduce((sum, item) => sum + Number(item.tax || 0), 0);
    const total = Math.max(0, subtotal - discount + tax);
    const paid = (form.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return { subtotal, discount, tax, total, paid, remaining: Math.max(0, total - paid) };
}

function normalizeThemeColor(value) {
    if (!value || typeof value !== "string") return "#1d4ed8";
    const trimmed = value.trim().toLowerCase();
    const named = {
        blue: "#1d4ed8",
        indigo: "#4338ca",
        teal: "#0f766e",
        emerald: "#059669",
        orange: "#ea580c",
        rose: "#e11d48",
        purple: "#7c3aed",
        cyan: "#0891b2",
        green: "#16a34a",
        red: "#dc2626",
    };
    if (named[trimmed]) return named[trimmed];
    const hex = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
    if (/^[0-9a-f]{3}$/.test(hex)) return `#${hex.split("").map((c) => c + c).join("")}`;
    if (/^[0-9a-f]{6}$/.test(hex)) return `#${hex}`;
    return "#1d4ed8";
}

export default function Invoices() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [template, setTemplate] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [clientId, setClientId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [dueFrom, setDueFrom] = useState("");
    const [dueTo, setDueTo] = useState("");
    const [amountMin, setAmountMin] = useState("");
    const [amountMax, setAmountMax] = useState("");
    const [sort, setSort] = useState("-created_at");
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [paymentFor, setPaymentFor] = useState(null);
    const [templateOpen, setTemplateOpen] = useState(false);

    const loadMeta = useCallback(async () => {
        try {
            const [cl, pr, tpl, dash] = await Promise.all([
                api.get("/clients"),
                api.get("/projects"),
                api.get("/invoice-template"),
                api.get("/invoices/dashboard"),
            ]);
            setClients(normalizeListResponse(cl.data).items);
            setProjects(normalizeListResponse(pr.data).items);
            setTemplate(tpl.data || null);
            setDashboard(dash.data || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load invoices");
        }
    }, []);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ page, limit: 10 });
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            if (clientId) params.set("client_id", clientId);
            if (projectId) params.set("project_id", projectId);
            if (dueFrom) params.set("due_from", dueFrom);
            if (dueTo) params.set("due_to", dueTo);
            if (amountMin !== "") params.set("amount_min", amountMin);
            if (amountMax !== "") params.set("amount_max", amountMax);
            if (sort) params.set("sort", sort);
            const inv = await api.get(`/invoices?${params.toString()}`);
            const normalized = normalizeListResponse(inv.data);
            setItems(normalized.items);
            setCounts(normalized.counts);
            setTotal(normalized.total);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load invoices");
        }
    }, [amountMax, amountMin, clientId, dueFrom, dueTo, page, projectId, q, sort, status]);

    useEffect(() => { loadMeta(); }, [loadMeta]);
    useEffect(() => { load(); }, [load]);

    const viewState = { q, status, clientId, projectId, dueFrom, dueTo, amountMin, amountMax, sort };
    const applyState = (next) => {
        setQ(next.q || "");
        setStatus(next.status || "");
        setClientId(next.clientId || "");
        setProjectId(next.projectId || "");
        setDueFrom(next.dueFrom || "");
        setDueTo(next.dueTo || "");
        setAmountMin(next.amountMin || "");
        setAmountMax(next.amountMax || "");
        setSort(next.sort || "-created_at");
        setPage(1);
    };

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (inv) => { setEditing(inv); setFormOpen(true); };

    const sendEmail = async (id) => {
        try {
            await api.post(`/invoices/${id}/send-email`);
            toast.success("Invoice emailed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to send invoice");
        }
    };

    const downloadPdf = (id) => {
        window.open(backendUrl(`/api/invoices/${id}/pdf`), "_blank", "noopener,noreferrer");
    };

    const duplicate = async (id) => {
        try {
            await api.post(`/invoices/${id}/duplicate`);
            toast.success("Invoice duplicated");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Duplicate failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this invoice?")) return;
        try {
            await api.delete(`/invoices/${id}`);
            toast.success("Invoice deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Delete failed");
        }
    };

    return (
        <div className="space-y-6" data-testid={INVOICES.root}>
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Billing</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Invoices</h1>
                    <p className="text-sm text-muted-foreground mt-2">Track billing, payments, PDFs, and invoice templates.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setTemplateOpen(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-border bg-card hover:bg-secondary text-sm">
                        <ReceiptText className="h-4 w-4" /> Template
                    </button>
                    <button onClick={openCreate} data-testid={INVOICES.createButton} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                        <Plus className="h-4 w-4" /> New Invoice
                    </button>
                </div>
            </div>

            <StatusCountCards
                cards={[
                    { label: "Total Invoices", value: total, hint: "All matching records" },
                    { label: "Draft", value: counts.draft || 0 },
                    { label: "Sent", value: counts.sent || 0 },
                    { label: "Paid", value: counts.paid || 0 },
                    { label: "Overdue", value: counts.overdue || 0 },
                    { label: "Cancelled", value: counts.cancelled || 0 },
                ]}
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
            />

            {dashboard && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        ["Pending Payments", dashboard.pending_payments],
                        ["Overdue Payments", dashboard.overdue_payments],
                        ["Upcoming Payments", dashboard.upcoming_payments],
                        ["Revenue Collected", Object.entries(dashboard.revenue_by_currency || {}).map(([currency, value]) => `${currency} ${Number(value).toLocaleString()}`).join(" · ") || "—"],
                    ].map(([label, value]) => (
                        <div key={label} className="border border-border rounded-md bg-card p-5">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</div>
                            <div className="font-heading text-2xl font-bold mt-2">{value}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search invoice, client, project…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All statuses</option>
                    {["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={clientId} onChange={(e) => { setPage(1); setClientId(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
                </select>
                <SavedViewsBar
                    storageKey="crm.invoices.views"
                    state={viewState}
                    applyState={applyState}
                    defaultState={{ q: "", status: "", clientId: "", projectId: "", dueFrom: "", dueTo: "", amountMin: "", amountMax: "", sort: "-created_at" }}
                />
                <select value={projectId} onChange={(e) => { setPage(1); setProjectId(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All projects</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>)}
                </select>
                <input type="date" value={dueFrom} onChange={(e) => { setPage(1); setDueFrom(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="date" value={dueTo} onChange={(e) => { setPage(1); setDueTo(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="number" value={amountMin} onChange={(e) => { setPage(1); setAmountMin(e.target.value); }} placeholder="Min amount" className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="number" value={amountMax} onChange={(e) => { setPage(1); setAmountMax(e.target.value); }} placeholder="Max amount" className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm md:col-span-3">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <div className="border border-border rounded-md bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                            <th className="px-4 py-3">Invoice</th>
                            <th className="px-4 py-3">Client</th>
                            <th className="px-4 py-3">Issue</th>
                            <th className="px-4 py-3">Due</th>
                            <th className="px-4 py-3">Total</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Paid</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No invoices found.</td></tr>}
                        {items.map((inv) => {
                            const client = clients.find((c) => c.id === inv.client_id);
                            const totals = calcTotals(inv);
                            return (
                                <tr key={inv.id} data-testid={INVOICES.row} className={`border-b border-border last:border-0 hover:brightness-[0.99] transition-colors ${getStatusToneClasses(inv.status, { group: "payment", variant: "row" })}`}>
                                    <td className="px-4 py-3 font-medium"><HighlightText text={inv.invoice_number} query={q} /></td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium"><HighlightText text={inv.billing_name || client?.company || "—"} query={q} /></div>
                                        <div className="text-[11px] text-muted-foreground"><HighlightText text={inv.billing_contact || client?.client_name || "—"} query={q} /></div>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{new Date(inv.issue_date).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs font-mono">{new Date(inv.due_date).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 font-mono tabular-nums">{inv.currency || "USD"} {totals.total.toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(inv.status, { group: "payment", variant: "badge" })}`}>
                                            {getStatusLabel(inv.status)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">{inv.currency || "USD"} {totals.paid.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button aria-label="Edit invoice" title="Edit invoice" onClick={() => openEdit(inv)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Edit3 className="h-3.5 w-3.5" /></button>
                                            <button aria-label="Email invoice PDF" title="Email invoice PDF" onClick={() => sendEmail(inv.id)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Mail className="h-3.5 w-3.5" /></button>
                                            <button aria-label="Open invoice PDF" title="Open invoice PDF" onClick={() => downloadPdf(inv.id)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Printer className="h-3.5 w-3.5" /></button>
                                            <button aria-label="Duplicate invoice" title="Duplicate invoice" onClick={() => duplicate(inv.id)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><Copy className="h-3.5 w-3.5" /></button>
                                            <button aria-label="Record payment" title="Record payment" onClick={() => setPaymentFor(inv)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-secondary"><WalletCards className="h-3.5 w-3.5" /></button>
                                            <button aria-label="Delete invoice" title="Delete invoice" onClick={() => remove(inv.id)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <PaginationBar total={total} page={page} limit={10} onPageChange={setPage} />
            </div>

            {formOpen && (
                <InvoiceModal
                    invoice={editing}
                    clients={clients}
                    projects={projects}
                    template={template}
                    onClose={() => { setFormOpen(false); setEditing(null); }}
                    onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
                />
            )}
            {paymentFor && (
                <PaymentModal
                    invoice={paymentFor}
                    onClose={() => setPaymentFor(null)}
                    onSaved={() => { setPaymentFor(null); load(); }}
                />
            )}
            {templateOpen && (
                <TemplateModal
                    template={template}
                    onClose={() => setTemplateOpen(false)}
                    onSaved={async () => { setTemplateOpen(false); await load(); }}
                />
            )}
        </div>
    );
}

function InvoiceModal({ invoice, clients, projects, template, onClose, onSaved }) {
    const [form, setForm] = useState(() => normalizeInvoiceForm(invoice, template));
    const [busy, setBusy] = useState(false);

    const clientProjects = useMemo(() => projects.filter((p) => p.client_id === form.client_id), [projects, form.client_id]);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...form,
                issue_date: new Date(form.issue_date).toISOString(),
                due_date: new Date(form.due_date).toISOString(),
                items: (form.items || []).map((item) => ({
                    ...item,
                    quantity: Number(item.quantity) || 0,
                    unit_price: Number(item.unit_price) || 0,
                    discount: Number(item.discount) || 0,
                    tax: Number(item.tax) || 0,
                })),
            };
            if (invoice?.id) await api.patch(`/invoices/${invoice.id}`, payload);
            else await api.post("/invoices", payload);
            toast.success(invoice?.id ? "Invoice updated" : "Invoice created");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const updateItem = (index, patch) => {
        const items = [...(form.items || [])];
        items[index] = { ...items[index], ...patch };
        setForm({ ...form, items });
    };

    const addItem = () => setForm({ ...form, items: [...(form.items || []), emptyItem()] });
    const removeItem = (index) => setForm({ ...form, items: (form.items || []).filter((_, i) => i !== index) });
    const totals = calcTotals(form);

    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-6xl bg-card border border-border rounded-md shadow-xl overflow-hidden">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">{invoice?.id ? "Edit Invoice" : "Create Invoice"}</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">{invoice?.id ? invoice.invoice_number : "New invoice"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2 space-y-4">
                            <div className="rounded-md border border-border p-4 space-y-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Invoice details</div>
                                        <div className="text-sm text-muted-foreground">Number, dates, and status for this invoice.</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <label className="space-y-1.5 md:col-span-2">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Invoice Number</span>
                                        <input value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className={inputCls} placeholder="Auto-generated if left blank" />
                                    </label>
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Issue Date</span>
                                        <input required type="date" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className={inputCls} />
                                    </label>
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Due Date</span>
                                        <input required min={form.issue_date} type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
                                    </label>
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Status</span>
                                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                                            {["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </label>
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Payment Terms</span>
                                        <input value={form.payment_terms || ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className={inputCls} placeholder="Net 15, Net 30, Due on receipt..." />
                                    </label>
                                </div>
                            </div>

                            <div className="rounded-md border border-border p-4 space-y-4">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Client connection</div>
                                    <div className="text-sm text-muted-foreground">Pick the client and project this invoice belongs to.</div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Client</span>
                                        <select value={form.client_id || ""} onChange={(e) => { const client = clients.find(c => c.id === e.target.value) || {}; setForm({ ...form, client_id: e.target.value, project_id: "", billing_name: client.company || "", billing_contact: client.client_name || "", billing_email: client.client_email || client.email || client.company_email || "", billing_phone: client.phone || "", billing_address: client.billing_address || [client.city, client.state, client.country].filter(Boolean).join(", "), billing_tax_id: client.tax_id || "" }); }} className={inputCls}>
                                            <option value="">Select client</option>
                                            {clients.map((c) => <option key={c.id} value={c.id}>{c.company || c.client_name || c.company_email || c.id}</option>)}
                                        </select>
                                        {!clients.length && <div className="text-[11px] text-muted-foreground">No clients available yet. Convert a lead to a client first.</div>}
                                    </label>
                                    <label className="space-y-1.5">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Project</span>
                                        <select value={form.project_id || ""} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={inputCls} disabled={!form.client_id}>
                                            <option value="">{form.client_id ? "Optional" : "Select a client first"}</option>
                                            {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>)}
                                        </select>
                                        {form.client_id && clientProjects.length === 0 && <div className="text-[11px] text-muted-foreground">No projects linked to this client yet.</div>}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="rounded-md border border-border p-4 bg-secondary/20 space-y-3">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Template snapshot</div>
                                    <div className="text-sm text-muted-foreground">This invoice inherits the active template details.</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="h-16 w-16 rounded-md border border-border bg-background overflow-hidden flex items-center justify-center">
                                        {form.company_logo ? (
                                            <img src={form.company_logo} alt="Company logo" className="h-full w-full object-contain p-1" />
                                        ) : (
                                            <span className="text-[10px] text-muted-foreground text-center px-2">No logo</span>
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{form.company_details || template?.company_details || "Company details"}</div>
                                        <div className="text-[11px] text-muted-foreground line-clamp-2">{form.footer || template?.footer || "Footer text"}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-md border border-border p-4 bg-secondary/20">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Totals</div>
                                <div className="mt-2 space-y-1 text-sm">
                                    <div className="flex justify-between"><span>Subtotal</span><span>{form.currency} {totals.subtotal.toLocaleString()}</span></div>
                                    <div className="flex justify-between"><span>Discount</span><span>{form.currency} {totals.discount.toLocaleString()}</span></div>
                                    <div className="flex justify-between"><span>Tax</span><span>{form.currency} {totals.tax.toLocaleString()}</span></div>
                                    <div className="flex justify-between font-semibold pt-2 border-t border-border"><span>Total</span><span>{form.currency} {totals.total.toLocaleString()}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="rounded-md border border-border p-4 space-y-4">
                        <h4 className="font-semibold">Billing details & payment instructions</h4>
                        <p className="text-sm text-muted-foreground">These details are saved with the invoice and included in the client PDF. You can also invoice a client without linking a CRM record.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field title="Currency" value={form.currency} onChange={v => setForm({ ...form, currency: v })} options={["USD", "INR", "EUR", "GBP", "AED", "CAD", "AUD"]} />
                            {[['billing_name', 'Client legal / company name', true], ['billing_contact', 'Contact name'], ['billing_email', 'Billing email'], ['billing_phone', 'Phone'], ['billing_address', 'Billing address (street, city, postal code, country)', true], ['billing_tax_id', 'Client tax ID / GSTIN'], ['supplier_tax_id', 'Your company tax ID / GSTIN'], ['purchase_order', 'Purchase order / reference'], ['place_of_supply', 'Place of supply'], ['company_details', 'Your company name & address', true], ['bank_details', 'Payment instructions (bank, account, IFSC / SWIFT / IBAN)'], ['terms_and_conditions', 'Terms & conditions']].map(([key, title, required]) => <Field key={key} title={title} value={form[key]} required={required} type={['billing_address', 'company_details', 'bank_details', 'terms_and_conditions'].includes(key) ? 'textarea' : key === 'billing_email' ? 'email' : 'text'} onChange={v => setForm({ ...form, [key]: v })} />)}
                        </div>
                    </section>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h4 className="font-semibold">Items</h4>
                            <button type="button" onClick={addItem} className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border hover:bg-secondary text-xs">
                                <FilePlus2 className="h-3.5 w-3.5" /> Add Item
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground">Quantity × unit rate − discount amount + tax amount. Discount and tax are amounts in {form.currency}, not percentages.</p>
                        <div className="space-y-3">
                            {(form.items || []).map((item, idx) => (
                                <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-border rounded-md p-3">
                                    <div className="col-span-2"><Field title="Service description" required value={item.description} onChange={v => updateItem(idx, { description: v })} /></div>
                                    <div className="col-span-2"><Field title="Service code / HSN / SAC" value={item.service_code} onChange={v => updateItem(idx, { service_code: v })} /></div>
                                    {[['quantity', 'Quantity', 0.01], ['unit_price', 'Unit price', 0], ['discount', 'Discount amount', 0], ['tax', 'Tax amount', 0]].map(([key, title, min]) => <Field key={key} title={title} type="number" min={min} required value={item[key]} onChange={v => updateItem(idx, { [key]: v })} />)}
                                    <button type="button" disabled={form.items.length === 1} aria-label="Remove item" onClick={() => removeItem(idx)} className="inline-flex items-center gap-2 text-sm text-destructive disabled:opacity-40"><Trash2 className="h-4 w-4" /> Remove item</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="space-y-1.5 md:col-span-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Notes</span>
                            <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
                        </label>
                        <div className="border border-border rounded-md p-4 bg-secondary/20">
                            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Template</div>
                            <div className="mt-2 text-xs text-muted-foreground space-y-2">
                                <div>{template?.company_details || "Default company details"}</div>
                                <div>{template?.terms_conditions || "Default terms and conditions"}</div>
                                <div>{template?.footer || "Default footer"}</div>
                            </div>
                        </div>
                        {(form.payments || []).length > 0 && (
                            <div className="border border-border rounded-md p-4 bg-secondary/20 space-y-3">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Payment history</div>
                                <div className="space-y-2">
                                    {(form.payments || []).map((payment, idx) => {
                                        const tone = payment.remaining_balance <= 0 ? "paid" : "partially_paid";
                                        return (
                                            <div key={`${payment.payment_date || "payment"}-${idx}`} className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${getStatusToneClasses(tone, { group: "payment", variant: "surface" })}`}>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium">{form.currency} {Number(payment.amount || 0).toLocaleString()}</div>
                                                    <div className="text-[11px] text-muted-foreground font-mono">{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : "—"} · {payment.method || "—"}</div>
                                                </div>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(tone, { group: "payment", variant: "badge" })}`}>
                                                    {getStatusLabel(tone)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button disabled={busy} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Save Invoice"}</button>
                </div>
            </form>
        </div>
    );
}

function PaymentModal({ invoice, onClose, onSaved }) {
    const [form, setForm] = useState({ amount: "", payment_date: new Date().toISOString().slice(0, 10), method: "bank_transfer", transaction_id: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/invoices/${invoice.id}/payments`, {
                ...form,
                payment_date: new Date(form.payment_date).toISOString(),
                amount: Number(form.amount) || 0,
            });
            toast.success("Payment recorded");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-card border border-border rounded-md shadow-xl">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Payment</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">{invoice.invoice_number}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <label className="space-y-1.5 block">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Amount</span>
                        <input required min="0.01" max={invoice.remaining_balance} step="0.01" type="number" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Payment Date</span>
                        <input required type="date" className={inputCls} value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Method</span>
                        <select className={inputCls} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                            {["cash", "bank_transfer", "card", "upi", "cheque", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Transaction ID</span>
                        <input className={inputCls} value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} />
                    </label>
                    <label className="space-y-1.5 block">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Notes</span>
                        <textarea className={inputCls} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </label>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button disabled={busy} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Record Payment"}</button>
                </div>
            </form>
        </div>
    );
}

function TemplateModal({ template, onClose, onSaved }) {
    const [form, setForm] = useState(template || {});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm(template || {});
    }, [template]);

    const logoPreview = form.company_logo || "";
    const themeColor = normalizeThemeColor(form.color_theme || template?.color_theme || "#1d4ed8");

    const updateLogo = async (file) => {
        if (!file) return;
        try {
            const dataUrl = await fileToDataUrl(file);
            setForm({ ...form, company_logo: dataUrl });
        } catch (err) {
            toast.error(err.message || "Failed to load logo");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.put("/invoice-template", form);
            toast.success("Invoice template updated");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl bg-card border border-border rounded-md shadow-xl">
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Template</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">Invoice Template</h3>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1 rounded-md border border-border bg-secondary/20 p-4 space-y-3">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Logo</div>
                            <div className="text-sm text-muted-foreground">Upload a real image file for the invoice header.</div>
                        </div>
                        <div className="aspect-[4/3] rounded-md border border-dashed border-border bg-card overflow-hidden flex items-center justify-center">
                            {logoPreview ? (
                                <img src={logoPreview} alt="Company logo preview" className="h-full w-full object-contain p-3" />
                            ) : (
                                <div className="text-center text-xs text-muted-foreground px-4">No logo uploaded yet</div>
                            )}
                        </div>
                        <label className="inline-flex cursor-pointer items-center justify-center h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                            Upload image
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => updateLogo(e.target.files?.[0])} />
                        </label>
                        {logoPreview && (
                            <button type="button" onClick={() => setForm({ ...form, company_logo: "" })} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">
                                Remove logo
                            </button>
                        )}
                        <div className="rounded-md border border-border bg-card p-3 space-y-2">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Preview</div>
                            <div className="rounded-md overflow-hidden border border-border">
                                <div className="h-12" style={{ backgroundColor: themeColor }} />
                                <div className="p-3 space-y-1">
                                    <div className="text-sm font-semibold">Invoice preview</div>
                                    <div className="text-[11px] text-muted-foreground">This color is applied to the PDF header and accent bars.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="space-y-1.5 md:col-span-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Company Details</span>
                            <textarea rows={3} value={form.company_details || ""} onChange={(e) => setForm({ ...form, company_details: e.target.value })} className={inputCls} placeholder="Company name, address, GST, phone..." />
                        </label>
                        <label className="space-y-1.5 md:col-span-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Color Theme</span>
                            <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3">
                                <input
                                    type="color"
                                    value={themeColor}
                                    onChange={(e) => setForm({ ...form, color_theme: e.target.value })}
                                    className="h-10 w-full rounded-md border border-input bg-card p-1 cursor-pointer"
                                    aria-label="Pick invoice theme color"
                                />
                                <input
                                    value={form.color_theme || ""}
                                    onChange={(e) => setForm({ ...form, color_theme: e.target.value })}
                                    className={inputCls}
                                    placeholder="#1d4ed8, blue, teal, rose..."
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {THEME_PRESETS.map((preset) => (
                                    <button
                                        type="button"
                                        key={preset.value}
                                        onClick={() => setForm({ ...form, color_theme: preset.value })}
                                        className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-secondary transition-colors"
                                    >
                                        <span className="h-3 w-3 rounded-full border border-border" style={{ backgroundColor: preset.value }} />
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        </label>
                        <label className="space-y-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Invoice Layout</span>
                            <input value={form.invoice_layout || ""} onChange={(e) => setForm({ ...form, invoice_layout: e.target.value })} className={inputCls} placeholder="standard, modern, compact..." />
                        </label>
                        <label className="space-y-1.5 md:col-span-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Footer</span>
                            <textarea rows={3} value={form.footer || ""} onChange={(e) => setForm({ ...form, footer: e.target.value })} className={inputCls} placeholder="Footer note shown on invoices" />
                        </label>
                        <label className="space-y-1.5 md:col-span-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Terms & Conditions</span>
                            <textarea rows={4} value={form.terms_conditions || ""} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} className={inputCls} placeholder="Payment terms, late fees, policies..." />
                        </label>
                    </div>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm">Cancel</button>
                    <button disabled={busy} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Saving…" : "Save Template"}</button>
                </div>
            </form>
        </div>
    );
}
